import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Very lightweight US timezone inference based on address text (mock)
// Returns UTC offset in hours for summer (DST) to keep it simple for June
export function inferTzOffsetFromAddress(address = "") {
  const a = address.toLowerCase();
  const isMatch = (arr) => arr.some(k => a.includes(k));
  // Pacific (approx DST)
  if (isMatch([" ca ", " california", "los angeles", "san francisco", "san diego", ", ca", "seattle", ", wa", "washington state"])) return -7;
  // Mountain (approx DST)
  if (isMatch([" denver", ", co", " colorado", "utah", "salt lake"])) return -6;
  // Arizona (no DST – treat as -7 for simplicity)
  if (isMatch([" phoenix", ", az", " arizona"])) return -7;
  // Central (approx DST) – default for Austin/TX
  if (isMatch([" austin", ", tx", " texas", "dallas", "houston", "san antonio"])) return -5;
  // Eastern (approx DST)
  if (isMatch([" new york", ", ny", " boston", ", ma", " miami", ", fl", "atlanta", ", ga"])) return -4;
  // Fallback to Central
  return -5;
}

// Compute upcoming cutoff UTC epoch for a vendor using its configured weekday/hour/minute
// weekday: 0..6 (Sunday=0)
export function computeVendorCutoffEpoch(company, address = "") {
  const w = typeof company?.selection_deadline_weekday === 'number' ? company.selection_deadline_weekday : 4; // Thu
  const hh = typeof company?.selection_deadline_hour === 'number' ? company.selection_deadline_hour : 23;
  const mm = typeof company?.selection_deadline_minute === 'number' ? company.selection_deadline_minute : 59;
  const tz = inferTzOffsetFromAddress(address);

  const now = new Date();
  const shiftedNow = new Date(now.getTime() + tz * 3600000); // target-local as UTC
  const y = shiftedNow.getUTCFullYear();
  const m = shiftedNow.getUTCMonth();
  const d = shiftedNow.getUTCDate();
  const dow = shiftedNow.getUTCDay();

  let addDays = (w - dow);
  if (addDays < 0) addDays += 7;
  // Build tentative cutoff this week
  const cutoffLocalUTC = Date.UTC(y, m, d + addDays, hh, mm, 0);
  // Convert local target time to real UTC by subtracting tz offset
  const cutoffUTC = cutoffLocalUTC - tz * 3600000;

  // If our target-now already past cutoff, move to next week
  if ((now.getTime() + tz * 3600000) > (cutoffUTC + tz * 3600000)) {
    const nextWeekCutoffLocalUTC = Date.UTC(y, m, d + addDays + 7, hh, mm, 0);
    return nextWeekCutoffLocalUTC - tz * 3600000;
  }
  return cutoffUTC;
}

export function isAfterCutoff(company, address = "") {
  const cutoff = computeVendorCutoffEpoch(company, address);
  return Date.now() >= cutoff;
}

export function formatCutoffDisplay(company, address = "") {
  const epoch = computeVendorCutoffEpoch(company, address);
  const dt = new Date(epoch);
  // Display in user's local time; add hint
  return dt.toLocaleString(undefined, { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}


// Diagnostics mode utilities
export function isDiagnosticsMode() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    if (qs.get("debug") === "1") return true;
    const flag = localStorage.getItem("prepmate_debug");
    if (flag === "1") return true;
  } catch {}
  return false;
}

export function shouldLogVerbose() {
  const isDev = (typeof import.meta !== "undefined" && import.meta.env && !!import.meta.env.DEV) || (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production");
  return isDev || isDiagnosticsMode();
}

export function diagLog(ns, ...args) {
  if (shouldLogVerbose()) {
    try { console.log(`[PrepMate][${ns}]`, ...args); } catch {}
  }
}

export function diagError(ns, ...args) {
  if (shouldLogVerbose()) {
    try { console.error(`[PrepMate][${ns}]`, ...args); } catch {}
  }
}

export function handleSubscriptionConflict(err, toast, navigate){
  const msg = err?.response?.data?.detail || "You already have an active subscription. Please manage it in Dashboard (pause/cancel) before starting a new one.";
  if (err?.response?.status === 409) {
    try { toast?.({ title: "Cannot start new subscription", description: msg }); } catch {}
    try { navigate?.("/dashboard"); } catch {}
    return true;
  }
  return false;
}
